import React, { useState } from 'react';
import { 
  Card, 
  Upload, 
  Button, 
  Select, 
  Slider, 
  Row, 
  Col, 
  Typography, 
  Space, 
  message,
  Progress,
  Image,
  Divider
} from 'antd';
import { 
  UploadOutlined, 
  PictureOutlined, 
  ZoomInOutlined,
  DownloadOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';

const { Title, Text } = Typography;
const { Option } = Select;

interface UpscaleParams {
  scale: number;
  model: string;
  denoise: number;
  format: string;
}

const ImageUpscalePage: React.FC = () => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<string | null>(null);
  const [params, setParams] = useState<UpscaleParams>({
    scale: 2,
    model: 'esrgan',
    denoise: 0.5,
    format: 'png'
  });

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    fileList,
    beforeUpload: (file) => {
      const isImage = file.type?.startsWith('image/');
      if (!isImage) {
        message.error('只能上传图片文件！');
        return false;
      }
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('图片大小不能超过 10MB！');
        return false;
      }
      return false; // 阻止自动上传
    },
    onChange: (info) => {
      setFileList(info.fileList.slice(-1)); // 只保留最后一个文件
      setResult(null); // 清除之前的结果
    },
    onRemove: () => {
      setFileList([]);
      setResult(null);
    }
  };

  const handleUpscale = async () => {
    if (fileList.length === 0) {
      message.error('请先上传图片！');
      return;
    }

    setProcessing(true);
    setProgress(0);

    try {
      // 模拟处理进度
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + Math.random() * 15;
        });
      }, 500);

      // 这里应该调用实际的API
      // const formData = new FormData();
      // formData.append('file', fileList[0].originFileObj!);
      // formData.append('params', JSON.stringify(params));
      // const response = await imageUpscaleApi.upscale(formData);

      // 模拟API调用
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      clearInterval(progressInterval);
      setProgress(100);
      
      // 模拟结果
      setResult('https://via.placeholder.com/800x600/4CAF50/white?text=Upscaled+Image');
      message.success('图片放大完成！');
    } catch (error) {
      console.error('图片放大失败:', error);
      message.error('图片放大失败，请重试！');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = () => {
    if (result) {
      const link = document.createElement('a');
      link.href = result;
      link.download = `upscaled_${Date.now()}.${params.format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success('下载开始！');
    }
  };

  const handleReset = () => {
    setFileList([]);
    setResult(null);
    setProgress(0);
    setProcessing(false);
  };

  return (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
          <ZoomInOutlined style={{ marginRight: '8px' }} />
          图片放大
        </Title>
        <Text type="secondary">使用AI技术智能放大图片，提升图片分辨率和清晰度</Text>
      </div>

      <Row gutter={[24, 24]}>
        {/* 左侧参数设置 */}
        <Col xs={24} lg={10}>
          <Card title={
            <Space>
              <PictureOutlined />
              图片上传与参数设置
            </Space>
          }>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              {/* 文件上传 */}
              <div>
                <Text strong>选择图片</Text>
                <Upload.Dragger {...uploadProps} style={{ marginTop: '8px' }}>
                  <p className="ant-upload-drag-icon">
                    <UploadOutlined />
                  </p>
                  <p className="ant-upload-text">点击或拖拽图片到此区域上传</p>
                  <p className="ant-upload-hint">
                    支持 JPG、PNG、WEBP 格式，文件大小不超过 10MB
                  </p>
                </Upload.Dragger>
              </div>

              <Divider />

              {/* 放大倍数 */}
              <div>
                <Text strong>放大倍数: {params.scale}x</Text>
                <Slider
                  min={1}
                  max={4}
                  step={0.5}
                  value={params.scale}
                  onChange={(value) => setParams(prev => ({ ...prev, scale: value }))}
                  marks={{
                    1: '1x',
                    2: '2x',
                    3: '3x',
                    4: '4x'
                  }}
                  style={{ marginTop: '8px' }}
                />
              </div>

              {/* 放大模型 */}
              <div>
                <Text strong>放大模型</Text>
                <Select
                  value={params.model}
                  onChange={(value) => setParams(prev => ({ ...prev, model: value }))}
                  style={{ width: '100%', marginTop: '8px' }}
                >
                  <Option value="esrgan">ESRGAN (通用)</Option>
                  <Option value="real-esrgan">Real-ESRGAN (真实照片)</Option>
                  <Option value="waifu2x">Waifu2x (动漫插画)</Option>
                  <Option value="srcnn">SRCNN (快速)</Option>
                </Select>
              </div>

              {/* 降噪强度 */}
              <div>
                <Text strong>降噪强度: {params.denoise}</Text>
                <Slider
                  min={0}
                  max={1}
                  step={0.1}
                  value={params.denoise}
                  onChange={(value) => setParams(prev => ({ ...prev, denoise: value }))}
                  marks={{
                    0: '无',
                    0.5: '中等',
                    1: '强'
                  }}
                  style={{ marginTop: '8px' }}
                />
              </div>

              {/* 输出格式 */}
              <div>
                <Text strong>输出格式</Text>
                <Select
                  value={params.format}
                  onChange={(value) => setParams(prev => ({ ...prev, format: value }))}
                  style={{ width: '100%', marginTop: '8px' }}
                >
                  <Option value="png">PNG (无损)</Option>
                  <Option value="jpg">JPG (压缩)</Option>
                  <Option value="webp">WebP (现代)</Option>
                </Select>
              </div>

              {/* 操作按钮 */}
              <Space style={{ width: '100%', justifyContent: 'center' }}>
                <Button
                  type="primary"
                  size="large"
                  icon={<ZoomInOutlined />}
                  onClick={handleUpscale}
                  loading={processing}
                  disabled={fileList.length === 0}
                >
                  开始放大
                </Button>
                <Button
                  size="large"
                  icon={<ReloadOutlined />}
                  onClick={handleReset}
                  disabled={processing}
                >
                  重置
                </Button>
              </Space>
            </Space>
          </Card>
        </Col>

        {/* 右侧预览和结果 */}
        <Col xs={24} lg={14}>
          <Card title={
            <Space>
              <PictureOutlined />
              预览与结果
            </Space>
          }>
            {processing && (
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <Progress 
                  type="circle" 
                  percent={Math.round(progress)} 
                  status={progress === 100 ? 'success' : 'active'}
                />
                <div style={{ marginTop: '16px' }}>
                  <Text>正在处理图片，请稍候...</Text>
                </div>
              </div>
            )}

            <Row gutter={[16, 16]}>
              {/* 原图预览 */}
              {fileList.length > 0 && (
                <Col xs={24} md={result ? 12 : 24}>
                  <Card size="small" title="原图" style={{ textAlign: 'center' }}>
                    <Image
                      src={URL.createObjectURL(fileList[0].originFileObj!)}
                      alt="原图"
                      style={{ maxWidth: '100%', maxHeight: '300px' }}
                      preview={{
                        mask: '点击预览'
                      }}
                    />
                    <div style={{ marginTop: '8px' }}>
                      <Text type="secondary">
                        {fileList[0].name}
                      </Text>
                    </div>
                  </Card>
                </Col>
              )}

              {/* 放大结果 */}
              {result && (
                <Col xs={24} md={12}>
                  <Card 
                    size="small" 
                    title="放大结果" 
                    style={{ textAlign: 'center' }}
                    extra={
                      <Button
                        type="primary"
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={handleDownload}
                      >
                        下载
                      </Button>
                    }
                  >
                    <Image
                      src={result}
                      alt="放大结果"
                      style={{ maxWidth: '100%', maxHeight: '300px' }}
                      preview={{
                        mask: '点击预览'
                      }}
                    />
                    <div style={{ marginTop: '8px' }}>
                      <Text type="secondary">
                        放大 {params.scale}x | {params.model.toUpperCase()}
                      </Text>
                    </div>
                  </Card>
                </Col>
              )}
            </Row>

            {!fileList.length && !processing && !result && (
              <div style={{ 
                textAlign: 'center', 
                padding: '60px 20px',
                color: '#999'
              }}>
                <PictureOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                <div>请先上传图片开始处理</div>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ImageUpscalePage;